import React, { useEffect, useId, useRef, useState } from 'react';
import { ExpectedStayRow } from '../../../../interfaces/stays';
import { InlineMessage, Presence, StatusLabel, listItemMotion } from '../../../../motion';
import { StatusPill } from '../../../status-pill/status-pill';
import { Stepper } from '../../../stepper/stepper';
import { ActionWithReason } from '../../dashboard/dashboard-shared-components/disabled-reason';
import {
  Actions,
  Confirm,
  ConfirmText,
  ExitItem,
  Input,
  Label,
  Row,
  RowAction,
  RowLead,
  RowMain,
  RowMark,
  RowMeta,
  RowName,
  confirmMotion,
} from '../../visitor-exit/visitor-exit-page.styled';
import { initialsOf } from '../../visitor-exit/desk-list';
import { InlineAction } from '../shared/id';
import { ConfirmAct, RowInlineError } from './expected-row';
import {
  describeStaySignIn,
  isStaySignOutable,
  isStaySignable,
  stayArrival,
  stayHostTag,
  stayGuestLines,
  stayCheckinPill,
  stayCheckinReason,
  stayDomId,
  stayFirstName,
  stayFullName,
  stayIdName,
  stayTicks,
  stayWhen,
  stayWindowPill,
} from './expected-stays-model';
import {
  ActionSlot,
  ConfirmButtons,
  ConfirmFields,
  ConfirmStack,
  FieldBlock,
  FieldLabel,
  InlineError,
  MetaStack,
  NotTodayText,
  RowActions,
  ScannedCaption,
  ScannedName,
  ScannedPass,
  TickRow,
} from './expected-page.styled';

/**
 * ONE SHORT-STAY GUEST ON EXPECTED TODAY (Lobbero Stays plan S3) — who, which
 * unit, when, whether their online check-in is done, and the one tap.
 *
 * The pass row's grammar exactly (expected-row.tsx): the row owns only what is
 * local to one confirm — the party, the note, the two "sign in anyway?" ticks,
 * reset every time the panel opens. The in-flight gate, the pending paint and
 * the server's answer live on the page, keyed by row.
 *
 * TWO TICKS, EACH SAID OUT LOUD. Outside the expected arrival time, and a
 * check-in not finished online, are two different things the desk takes
 * responsibility for; Confirm stays off until every one the row asks for is
 * ticked, and each is what the server receives (confirm_outside_window,
 * confirm_incomplete).
 *
 * ── FOCUS ── Opening a confirm moves focus INTO it; closing it puts focus back
 * on the row's own action.
 */

export interface StaySignInChoice {
  guests: number;
  note: string;
  confirmOutsideWindow: boolean;
  confirmIncomplete: boolean;
}

export interface ExpectedStayRowItemProps {
  row: ExpectedStayRow;
  index: number;
  layout: boolean;
  /** The BUILDING's zone: the desk shell is the building's clock. */
  zone: string | null | undefined;
  tone: 'plain' | 'picked';
  open: ConfirmAct | null;
  onToggle: (act: ConfirmAct) => void;
  onClose: () => void;
  blockedReason: string | null;
  blocked: boolean;
  pendingIn: boolean;
  pendingOut: boolean;
  /** The server asked for a tick the list did not (a 409 after a change). */
  forceOutside?: boolean;
  forceIncomplete?: boolean;
  inlineError: RowInlineError | null;
  onSignIn: (choice: StaySignInChoice) => void;
  onSignOut: () => void;
  onSignInAsVisitor: () => void;
}

const At: React.FC<{ at: string; children: React.ReactNode }> = ({ at, children }) => (
  <time dateTime={at} data-at={at}>
    {children}
  </time>
);

export const ExpectedStayRowItem: React.FC<ExpectedStayRowItemProps> = ({
  row,
  index,
  layout,
  zone,
  tone,
  open,
  onToggle,
  onClose,
  blockedReason,
  blocked,
  pendingIn,
  pendingOut,
  forceOutside = false,
  forceIncomplete = false,
  inlineError,
  onSignIn,
  onSignOut,
  onSignInAsVisitor,
}) => {
  const uid = useId();
  const sentenceId = `${uid}-sentence`;
  const noteInputId = `${uid}-note-input`;
  const outsideTickId = `${uid}-tick-outside`;
  const incompleteTickId = `${uid}-tick-incomplete`;

  const first = stayFirstName(row);
  const name = stayFullName(row);
  const idName = stayIdName(row);
  const signable = isStaySignable(row);
  const signOutable = isStaySignOutable(row);
  const hasActions = signable || signOutable;
  const maxGuests = Math.max(1, Number(row.party_size) || 1);
  const ticks = stayTicks(row, { outside: forceOutside, incomplete: forceIncomplete });

  const when = stayWhen(row, zone);
  const checkin = stayCheckinPill(row);
  const reason = stayCheckinReason(row);
  const windowPill = stayWindowPill(row);
  const arrival = stayArrival(row, zone);
  const guestLines = stayGuestLines(row, zone);

  const [guests, setGuests] = useState(maxGuests);
  const [note, setNote] = useState('');
  const [tickedOutside, setTickedOutside] = useState(false);
  const [tickedIncomplete, setTickedIncomplete] = useState(false);

  const inButton = useRef<HTMLButtonElement | null>(null);
  const outButton = useRef<HTMLButtonElement | null>(null);
  const panelIn = useRef<HTMLDivElement | null>(null);
  const panelOut = useRef<HTMLDivElement | null>(null);
  const lastOpen = useRef<ConfirmAct | null>(null);

  // A panel that opens starts clean: the whole party, no note, nothing ticked.
  const resetChoice = () => {
    setGuests(maxGuests);
    setNote('');
    setTickedOutside(false);
    setTickedIncomplete(false);
  };
  useEffect(() => {
    if (open === 'in') {
      setGuests(maxGuests);
      setNote('');
      setTickedOutside(false);
      setTickedIncomplete(false);
    }
    // Only on the panel opening: a re-read under an open panel keeps the
    // desk's choices.
  }, [open]);

  // The party can shrink under an open panel (the stay was edited).
  useEffect(() => {
    if (guests > maxGuests) setGuests(maxGuests);
  }, [guests, maxGuests]);

  // The panel is offered only while the guest can still be signed in: once a
  // re-read says they are inside, the in-panel goes and Sign out takes over.
  const inOpen = open === 'in' && signable;
  const outOpen = open === 'out' && signOutable;
  const shown: ConfirmAct | null = inOpen ? 'in' : outOpen ? 'out' : null;

  // FOCUS IN on open, and BACK to the row's action on close — on what is
  // actually SHOWN, so a panel withdrawn by a re-read hands focus back too.
  useEffect(() => {
    const was = lastOpen.current;
    lastOpen.current = shown;
    if (shown && shown !== was) {
      (shown === 'in' ? panelIn.current : panelOut.current)?.focus();

      return;
    }
    if (!shown && was) {
      const leaving = was === 'in' ? panelIn.current : panelOut.current;
      const active = document.activeElement;
      const focusWasHere =
        !active ||
        active === document.body ||
        (leaving !== null && leaving.contains(active));
      if (!focusWasHere) return;
      const back =
        (was === 'in' ? inButton.current : outButton.current) ||
        inButton.current ||
        outButton.current;
      back?.focus();
    }
  }, [shown]);

  const onPanelKey = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      onClose();
    }
  };

  const inBusy = pendingIn;
  const outBusy = pendingOut;
  const confirmInDisabled =
    inBusy ||
    blocked ||
    (ticks.outside && !tickedOutside) ||
    (ticks.incomplete && !tickedIncomplete);

  const errorBody = inlineError ? (
    <InlineError>
      {inlineError.kind === 'not_today' ? (
        <>
          <NotTodayText>{inlineError.text}</NotTodayText>
          <InlineAction type="button" onClick={onSignInAsVisitor}>
            Sign in as a visitor
          </InlineAction>
        </>
      ) : (
        <NotTodayText>{inlineError.text}</NotTodayText>
      )}
    </InlineError>
  ) : null;

  return (
    <ExitItem
      id={stayDomId(row)}
      data-reservation-id={row.reservation_id}
      data-kind="short_stay"
      data-linked={row.linked ? 'true' : undefined}
      {...listItemMotion(index)}
      layout={layout}
    >
      <Row tone={tone}>
        <RowLead>
          <RowMark tone={tone} aria-hidden="true">
            {initialsOf(name)}
          </RowMark>
          <RowMain>
            <RowName>{`Short-stay guest · ${row.first_name || first}`}</RowName>
            <RowMeta>
              {when.lead}
              {when.time ? <At at={row.arrival_at}>{when.time}</At> : null}
            </RowMeta>
            <MetaStack>
              {/* Plan S4 (R-S4.18): the host revoked this guest's access. The
                  desk still sees the arrival, and is told to ask the host. */}
              {row.access_revoked ? (
                <StatusPill tone="alert" dot>
                  Guest access revoked — check with the host
                </StatusPill>
              ) : null}
              <StatusPill tone={checkin.tone} dot>
                {checkin.text}
              </StatusPill>
              {stayHostTag(row) ? <RowMeta data-testid="stay-host-tag">{stayHostTag(row)}</RowMeta> : null}
              {reason ? <RowMeta>{reason}</RowMeta> : null}
              {guestLines.map(line => (
                <RowMeta key={line}>{line}</RowMeta>
              ))}
              {windowPill ? (
                <StatusPill tone={windowPill.tone} dot>
                  {windowPill.text}
                </StatusPill>
              ) : null}
              {arrival ? (
                <StatusPill tone="neutral" dot>
                  {arrival.lead}
                  <At at={arrival.at}>{arrival.time}</At>
                </StatusPill>
              ) : null}
            </MetaStack>
          </RowMain>
        </RowLead>
        {hasActions ? (
          <Actions>
            <ActionSlot>
              <ActionWithReason reason={blockedReason}>
                {describedBy => (
                  <RowActions>
                    {signable ? (
                      <RowAction
                        ref={inButton}
                        type="button"
                        variant={inOpen ? 'quiet' : 'primary'}
                        aria-label={inOpen ? undefined : `Sign in ${row.first_name || first}`}
                        aria-describedby={describedBy}
                        aria-expanded={inOpen}
                        disabled={(blocked && !inOpen) || inBusy}
                        onClick={() => {
                          if (!inOpen) resetChoice();
                          onToggle('in');
                        }}
                      >
                        {inOpen ? 'Cancel' : 'Sign in'}
                      </RowAction>
                    ) : null}
                    {signOutable ? (
                      <RowAction
                        ref={outButton}
                        type="button"
                        variant={outOpen ? 'quiet' : 'primary'}
                        aria-describedby={describedBy}
                        aria-expanded={outOpen}
                        disabled={(blocked && !outOpen) || outBusy}
                        onClick={() => onToggle('out')}
                      >
                        {outOpen ? 'Cancel' : 'Sign out'}
                      </RowAction>
                    ) : null}
                  </RowActions>
                )}
              </ActionWithReason>
            </ActionSlot>
          </Actions>
        ) : null}
      </Row>
      {/* An answer that closed its panel (already signed in, a re-read)
          stays readable under the row until the next press. */}
      {!inOpen && !outOpen ? <InlineMessage role="alert" message={errorBody} /> : null}
      <Presence>
        {inOpen ? (
          <Confirm key="confirm-in" {...confirmMotion}>
            <ConfirmStack
              ref={panelIn}
              tabIndex={-1}
              role="group"
              aria-labelledby={sentenceId}
              onKeyDown={onPanelKey}
            >
              <ScannedPass>
                <ScannedCaption>{idName ? 'Name on ID' : 'Name on booking'}</ScannedCaption>
                <ScannedName>{idName || name}</ScannedName>
              </ScannedPass>
              <ConfirmText id={sentenceId}>{describeStaySignIn(row)}</ConfirmText>
              <ConfirmFields>
                {maxGuests > 1 ? (
                  <FieldBlock>
                    <FieldLabel aria-hidden="true">People</FieldLabel>
                    <Stepper
                      value={guests}
                      min={1}
                      max={maxGuests}
                      label="people"
                      disabled={inBusy}
                      onChange={setGuests}
                    />
                  </FieldBlock>
                ) : null}
                <FieldBlock $grow>
                  <Label htmlFor={noteInputId}>Note (optional)</Label>
                  <Input
                    id={noteInputId}
                    value={note}
                    maxLength={280}
                    disabled={inBusy}
                    onChange={e => setNote(e.target.value)}
                  />
                </FieldBlock>
              </ConfirmFields>
              {ticks.outside ? (
                <TickRow htmlFor={outsideTickId}>
                  <input
                    id={outsideTickId}
                    type="checkbox"
                    checked={tickedOutside}
                    disabled={inBusy}
                    onChange={e => setTickedOutside(e.target.checked)}
                  />
                  <span>Outside the expected arrival time. Sign in anyway?</span>
                </TickRow>
              ) : null}
              {ticks.incomplete ? (
                <TickRow htmlFor={incompleteTickId}>
                  <input
                    id={incompleteTickId}
                    type="checkbox"
                    checked={tickedIncomplete}
                    disabled={inBusy}
                    onChange={e => setTickedIncomplete(e.target.checked)}
                  />
                  <span>
                    Check-in is not finished online. I checked their ID here. Sign in anyway?
                  </span>
                </TickRow>
              ) : null}
              <InlineMessage role="alert" message={errorBody} />
              <ConfirmButtons>
                <RowAction
                  type="button"
                  variant="primary"
                  disabled={confirmInDisabled}
                  aria-busy={inBusy || undefined}
                  onClick={() =>
                    onSignIn({
                      guests: Math.min(guests, maxGuests),
                      note,
                      confirmOutsideWindow: ticks.outside && tickedOutside,
                      confirmIncomplete: ticks.incomplete && tickedIncomplete,
                    })
                  }
                >
                  <StatusLabel
                    state={inBusy ? 'busy' : 'idle'}
                    labels={{ idle: `Confirm: sign ${first} in`, busy: 'Signing in…' }}
                  />
                </RowAction>
              </ConfirmButtons>
            </ConfirmStack>
          </Confirm>
        ) : null}
        {outOpen ? (
          <Confirm key="confirm-out" {...confirmMotion}>
            <ConfirmStack
              ref={panelOut}
              tabIndex={-1}
              role="group"
              aria-labelledby={sentenceId}
              onKeyDown={onPanelKey}
            >
              <ConfirmText id={sentenceId}>{`Record that ${name} left, now?`}</ConfirmText>
              <InlineMessage role="alert" message={errorBody} />
              <ConfirmButtons>
                <RowAction
                  type="button"
                  variant="danger"
                  disabled={outBusy || blocked}
                  aria-busy={outBusy || undefined}
                  onClick={onSignOut}
                >
                  <StatusLabel
                    state={outBusy ? 'busy' : 'idle'}
                    labels={{ idle: `Confirm: sign ${first} out`, busy: 'Signing out…' }}
                  />
                </RowAction>
              </ConfirmButtons>
            </ConfirmStack>
          </Confirm>
        ) : null}
      </Presence>
    </ExitItem>
  );
};
